import React, { useState, useEffect } from 'react';
import { FaArrowRight, FaArrowLeft, FaQuoteLeft, FaChartLine, FaHandHoldingUsd, FaHome, FaShieldAlt } from 'react-icons/fa';
import { useAuth } from '../../providers/auth.provider';

interface CarouselItem {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const LarkFinservCarousel: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const { partnerData } = useAuth();
  const carouselItems: CarouselItem[] = [
    {
      id: 1,
      title: "Quick Loan Approvals",
      description: "Get your loan Disbursed within 10 minutes with minimal documentation",
      icon: <FaHandHoldingUsd className="text-5xl mb-4" />
    },
    {
      id: 2,
      title: "Low Interest Rates",
      description: "We offer some of the most competitive interest rates in the market.",
      icon: <FaChartLine className="text-5xl mb-4" />
    },
    {
      id: 3,
      title: "Free Eligibility Checker",
      description: "Check your Eligibility in Free without impacting your CIBIL Score.",
      icon: <FaHome className="text-5xl mb-4" />
    },
    {
      id: 4,
      title: "Secure & Reliable",
      description: "Your data is protected with bank-level security protocols & RBI guidelines.",
      icon: <FaShieldAlt className="text-5xl mb-4" />
    },
    {
      id: 5,
      title: "Customer Testimonials",
      description: "LARK Finserv made my loan process incredibly simple. Highly recommended!",
      icon: <FaQuoteLeft className="text-5xl mb-4" />
    }
  ];

  const nextSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) => (prevIndex === carouselItems.length - 1 ? 0 : prevIndex + 1));
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  const prevSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) => (prevIndex === 0 ? carouselItems.length - 1 : prevIndex - 1));
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [currentIndex]);

  return (
    <div className="h-full w-full bg-gradient-to-r from-green-900 to-green-800 rounded-lg shadow-xl overflow-hidden">
      <div className="h-full flex flex-col justify-between p-8">
        {/* Logo and Brand */}
        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-white">{partnerData?.company_name ? partnerData?.company_name : <>LARK <span className="text-green-300">Finserv</span></>}</h2>
          <p className="text-green-200 text-sm">Financial solutions for your future</p>
        </div>

        {/* Carousel */}
        <div className="relative flex-grow flex items-center justify-center">
          <div className="overflow-hidden w-full max-w-md mx-auto">
            <div
              className="transition-transform duration-500 ease-in-out flex"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {carouselItems.map((item) => (
                <div
                  key={item.id}
                  className="w-full flex-shrink-0 text-center p-4"
                >
                  <div className="bg-green-800/50 p-6 rounded-lg border border-green-700 h-full flex flex-col items-center justify-center">
                    <div className="text-green-300">
                      {item.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                    <p className="text-green-100 text-sm md:text-base">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-0 transform -translate-y-1/2 top-1/2 bg-green-700 hover:bg-green-600 text-white p-2 rounded-l-lg transition-colors duration-300"
            aria-label="Previous slide"
          >
            <FaArrowLeft />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-0 transform -translate-y-1/2 top-1/2 bg-green-700 hover:bg-green-600 text-white p-2 rounded-r-lg transition-colors duration-300"
            aria-label="Next slide"
          >
            <FaArrowRight />
          </button>
        </div>

        {/* Carousel Indicators */}
        <div className="flex justify-center space-x-2 mt-4">
          {carouselItems.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                if (!isAnimating) {
                  setIsAnimating(true);
                  setCurrentIndex(index);
                  setTimeout(() => setIsAnimating(false), 500);
                }
              }}
              className={`h-2 rounded-full transition-all duration-300 ${currentIndex === index
                ? 'w-6 bg-green-300'
                : 'w-2 bg-green-600 hover:bg-green-500'
                }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LarkFinservCarousel;